"use client";;
import { Button } from "@/app/ui/forms/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, } from "@/app/ui/imported/command";
import { Popover, PopoverContent, PopoverTrigger, } from "@/app/ui/imported/popover";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import * as React from "react";
import stringSimilarity from "string-similarity-js";
import useFormItemReset from "../../hooks/useFormItemReset";
import Spinner from "../utils/Spinner";

export function MultiSelect({ id, options, placeholder, onChange, className, loading, defaultValues, max = Infinity }:
    {
        options: { value: string, label: React.ReactNode }[],
        placeholder?: string,
        id?: string,
        onChange?: (values: string[]) => any,
        className?: string,
        loading?: boolean,
        defaultValues?: string[]
        max?: number
    }) {
    const selectRef = React.useRef<HTMLSelectElement>(null);
    const [open, setOpen] = React.useState(false)
    const [values, setValues] = React.useState<Set<string>>(new Set(defaultValues))
    const [search, setSearch] = React.useState<string>("")

    useFormItemReset(selectRef, () => {
        setValues(new Set())
        setOpen(false)
    })

    function itemSelect(value: string) {
        setSearch(""); //reset search
        setValues(prevValues => {
            const newValues = new Set(prevValues);
            newValues.add(value);
            while (newValues.size > max) {
                //remove first item
                const firstKey = newValues.values().next().value;
                firstKey && newValues.delete(firstKey);
            }
            return newValues;
        });
    }

    function itemRemove(value: string) {
        setValues(prevValues => {
            const newValues = new Set(prevValues);
            newValues.delete(value);
            return newValues;
        });
    }

    React.useEffect(() => {
        open && onChange && onChange(Array.from(values));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values])

    return (
        <Popover open={ open } onOpenChange={ setOpen } >
            <select name={ id } id={ id } className="hidden invisible"
                ref={ selectRef }
                tabIndex={ -1 }
                value={ [...values] }
                multiple
                onChange={ () => { } }
            >
                <option disabled></option>
                {
                    [...values].map(v => <option key={ v } value={ v }>{ v }</option>)
                }
            </select>
            <PopoverTrigger asChild className={ className } disabled={ loading }>
                <Button role="combobox" aria-expanded={ open }
                    className="[&::-webkit-scrollbar:horizontal]:h-1 justify-between border border-nfPurple-600 bg-slate-950 hover:bg-nfPurple-950 p-2 flex flex-row gap-1 overflow-y-hidden overflow-x-auto max-w-full sm:max-w-sm">
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    { loading &&
                        <><span /><Spinner /><span /></> ||

                        values.size > 0 &&
                        [...values].map(opt => {
                            const option = options.find(o => o.value == opt);
                            return <span key={ option?.value } className="rounded border text-xs px-1">{ option?.label }</span>
                        }) ||
                        placeholder || "בחירה מרובה"
                    }
                    <span />

                </Button>
            </PopoverTrigger>
            <PopoverContent className="min-w-28 p-0">
                <Command filter={ (key, search, _) => {
                    if (search.length <= 1)
                        return 1;

                    const entry = options.find(o => o.value == key)
                    const value = (entry?.label as unknown as any)?.props?.children[1] || entry?.value;

                    return stringSimilarity(value, search) || search.includes(key) && 1 || 0
                } }>
                    <CommandInput placeholder={ `חפש ${placeholder || ""}..` } onInput={ (e) => setSearch((e.target as unknown as { value: string }).value) } />
                    <CommandList>
                        <CommandEmpty>רשימה ריקה</CommandEmpty>
                        <CommandGroup className="mb-1">
                            { options.sort(opt => !values.has(opt.value) ? 1 : -1).map(opt => (
                                <CommandItem
                                    className={ cn("mt-1", values.has(opt.value) && "bg-nfBlue-400/20") }
                                    key={ opt.value }
                                    value={ opt.value }
                                    onSelect={ (v) => {
                                        if (values.has(opt.value))
                                            itemRemove(v)
                                        else
                                            itemSelect(v)
                                    } }
                                >
                                    <Check
                                        className={ cn(
                                            "mr-2 h-4 w-4 text-nfBlue-400",
                                            values.has(opt.value) ? "opacity-100" : "opacity-0"
                                        ) }
                                    />
                                    <span>{ opt.label }</span>
                                </CommandItem>
                            )) }
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
